import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/react'
import { asChakraVariable, themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'

import { getName } from '@mangovoice/core'

import { sharedComponentColorKeys } from './_colors'

const { BoxShadow: BoxShadowDefaultColor } = themeColorSectionKeys.defaults
const Colors = sharedComponentColorKeys.AudioPlayer

export const enum AudioPlayerVariant {
  Stacked = 'stacked',
  Inline = 'inline',
  Analytics = 'analytics',
  Recording = 'recording',
}

const AudioPlayerParts = [
  'outerContainer',
  'errorContainer',
  'errorText',
  'loadingContainer',
  'container',
  'headerContainer',
  'titleText',
  'controlContainer',
  'controlButtonsContainer',
  'playbackContainer',
  'playbackSlider',
  'playbackSliderTrack',
  'playbackSliderFilledTrack',
  'playbackSliderThumbContainer',
  'playbackSliderThumb',
  'playbackSliderThumbInner',
  'playbackSliderVerticalBar',
  'playbackTimestampText',
  'playbackSpeedContainer',
  'playbackSpeedMenu',
  'playbackSpeedMenuItem',
  'playbackSpeedMenuItemText',
  'transcriptionContainer',
  'transcriptionTitleText',
  'transcriptionBodyText',
] as const

const helpers = createMultiStyleConfigHelpers(AudioPlayerParts)
export const AudioPlayerStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    outerContainer: {
      width: 'full',
    },
    errorContainer: {
      width: 'full',
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorText: {
      textStyle: textStyleKeys.Body1_Bold,
    },
    loadingContainer: {
      width: 'full',
      alignItems: 'center',
      justifyContent: 'center',
    },
    container: {
      flexDirection: 'column',
      padding: '1.25rem',
      gap: '0.5rem',
      borderRadius: '0.5rem',
      border: '1px solid',
      borderColor: Colors.Container.Border,
      backgroundColor: Colors.Container.Background,
    },
    headerContainer: {
      alignItems: 'center',
    },
    titleText: {
      textStyle: textStyleKeys.Body1_Bold,
    },
    controlContainer: {
      alignItems: 'center',
      gap: '1.75rem',
    },
    controlButtonsContainer: {
      alignItems: 'center',
      gap: '0.5rem',
    },
    playbackContainer: {
      alignItems: 'flex-start',
      flexGrow: 1,
      gap: '0',
      flexDirection: 'column',
    },
    playbackSlider: {
      height: '1.5rem',
    },
    playbackSliderTrack: {
      height: '0.5rem',
      borderRadius: '1rem',
      backgroundColor: Colors.Slider.Track.Background,
    },
    playbackSliderFilledTrack: {
      backgroundColor: Colors.Slider.FilledTrack.Background,
    },
    playbackSliderThumbContainer: {
      boxSizing: 'content-box',
      boxSize: '1rem',
      backgroundColor: 'transparent',
      padding: '2px',
      _hover: {
        backgroundColor: Colors.Slider.Thumb.Border,
      },
      transform: 'translate(0, -50%) !important',
    },
    playbackSliderThumb: {
      padding: '2px',
      backgroundColor: Colors.Slider.Thumb.Border,
      borderRadius: '1.25rem',
    },
    playbackSliderThumbInner: {
      width: '1rem',
      height: '1rem',
      borderRadius: '50%',
      backgroundColor: Colors.Slider.Thumb.Background,
    },
    playbackTimestampText: {
      minWidth: '2.125rem',
      textStyle: textStyleKeys.Body3,
      color: Colors.PlaybackTimestampText.FontColor,
    },
    playbackSpeedContainer: {},
    playbackSpeedMenu: {
      top: '2rem',
      right: 'px',
      zIndex: 2,
      flexDirection: 'column',
      justifyContent: 'center',
      width: '6.25rem',
      padding: '0.25rem',
      borderRadius: '0.5rem',
      border: '1px solid',
      borderColor: Colors.Dropdown.Menu.Border,
      backgroundColor: Colors.Dropdown.Menu.Background,
      boxShadow: `0px 4px 12px 0px ${asChakraVariable(BoxShadowDefaultColor)}`,
    },
    playbackSpeedMenuItem: {
      alignItems: 'center',
      paddingY: '0.5rem',
      paddingX: '0.75rem',
      borderRadius: '0.5rem',
      _hover: {
        backgroundColor: Colors.Dropdown.MenuItem.Hover.Background,
      },
      _active: {
        backgroundColor: Colors.Dropdown.MenuItem.Active.Background,
      },
    },
    playbackSpeedMenuItemText: {
      textStyle: textStyleKeys.Body1,
      userSelect: 'none',
    },
    transcriptionContainer: {
      flexDirection: 'column',
      padding: '1.25rem',
      gap: '0.5rem',
      borderRadius: '0.5rem',
      border: '1px solid',
      borderColor: Colors.TranscriptionContainer.Border,
      backgroundColor: Colors.TranscriptionContainer.Background,
    },
    transcriptionTitleText: {
      textStyle: textStyleKeys.Body1_Bold,
    },
    transcriptionBodyText: {
      textStyle: textStyleKeys.Body1,
    },
    playbackSliderVerticalBar: {
      display: 'none',
    },
  },
  variants: {
    [AudioPlayerVariant.Inline]: {
      container: {
        width: 'full',
      },
    },
    [AudioPlayerVariant.Stacked]: {
      container: {
        width: 'full',
      },
      controlContainer: {
        justifyContent: 'space-between',
        width: 'full',
      },
      controlButtonsContainer: {
        flexGrow: 1,
        justifyContent: 'center',
      },
    },
    [AudioPlayerVariant.Recording]: {
      container: {
        width: 'full',
        border: 'none',
        backgroundColor: 'transparent',
      },
      controlContainer: {
        justifyContent: 'space-between',
        width: 'full',
      },
      controlButtonsContainer: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingLeft: '4.25rem',
      },
    },
    [AudioPlayerVariant.Analytics]: {
      container: {
        width: 'full',
        border: 'none',
        backgroundColor: 'transparent',
        padding: '0 0 1.25rem 0',
      },
      controlContainer: {
        justifyContent: 'space-between',
        width: 'full',
      },
      controlButtonsContainer: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingLeft: '4.25rem',
      },
      playbackSliderVerticalBar: {
        display: 'block',
        position: 'absolute',
        top: '1.25rem',
        height: '3.375rem',
        width: '0.125rem',
        backgroundColor: 'gray.1300',
        borderRight: '1px solid',
        borderRightColor: 'gray.100',
        _groupHover: {
          top: '1.325rem',
          height: '3.3125rem',
        },
      },
    },
  },
})

export const useAudioPlayerStyle = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof AudioPlayerParts>(
    getName({ AudioPlayerStyle }),
    props,
  )
}
